import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import type { Question, OptionHint, Answers } from '../types';

// Declare global grecaptcha for TypeScript
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      render: (container: string, parameters: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback': () => void;
        'error-callback': () => void;
      }) => number;
      reset: (widgetId: number) => void;
    };
  }
}

interface QuestionCardProps {
  question: Question;
  nameInput: string;
  setNameInput: (value: string) => void;
  answers: Answers;
  onAnswer: (answer: string | OptionHint | number | object) => void;
  onNameSubmit: () => void;
  onContinueValues: (additionalText?: string) => void;
  onGoBack?: () => void;
  canGoBack?: boolean;
  isAnalyzing?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  nameInput,
  setNameInput,
  answers,
  onAnswer,
  onNameSubmit,
  onContinueValues,
  onGoBack,
  canGoBack = false,
  isAnalyzing = false
}) => {
  const [sliderValue, setSliderValue] = useState<number>(50);
  const [additionalInput, setAdditionalInput] = useState<string>('');
  const [textInput, setTextInput] = useState<string>(''); // Separate state for interests/context
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);
  const [question9SelectedAnswer, setQuestion9SelectedAnswer] = useState<string | OptionHint | null>(null);

  // Check if this is the first question (name input)
  const isFirstQuestion = question.type === 'name' && question.id === 1;

  // Debug reCAPTCHA environment variables
  const recaptchaEnvValue = import.meta.env.VITE_DISABLE_RECAPTCHA;
  if (isFirstQuestion) {
    console.log('🔍 reCAPTCHA Environment Debug:', {
      VITE_DISABLE_RECAPTCHA: recaptchaEnvValue,
      type: typeof recaptchaEnvValue,
      stringValue: String(recaptchaEnvValue),
      isExactlyTrue: recaptchaEnvValue === 'true',
      allEnvVars: Object.keys(import.meta.env)
    });
  }

  // Check if reCAPTCHA should be disabled (for development)
  const isRecaptchaDisabled = recaptchaEnvValue === 'true';
  const shouldShowRecaptcha = isFirstQuestion && !isRecaptchaDisabled;

  if (isFirstQuestion) {
    console.log('🔍 reCAPTCHA Decision:', {
      isFirstQuestion,
      isRecaptchaDisabled,
      shouldShowRecaptcha
    });
  }

  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(shouldShowRecaptcha);

  // Initialize reCAPTCHA for the first question
  useEffect(() => {
    if (!shouldShowRecaptcha) {
      setIsRecaptchaLoading(false);
      return;
    }

    let initAttempts = 0;
    const maxAttempts = 10;

    const initRecaptcha = () => {
      try {
        if (typeof window === 'undefined' || !window.grecaptcha || !window.grecaptcha.render) {
          initAttempts++;
          if (initAttempts < maxAttempts) {
            console.log(`reCAPTCHA not ready, attempt ${initAttempts}/${maxAttempts}`);
            setTimeout(initRecaptcha, 500);
          } else {
            console.error('reCAPTCHA failed to load after maximum attempts');
            setIsRecaptchaLoading(false);
          }
          return;
        }

        // Check if the DOM element exists before trying to render
        const containerElement = document.getElementById('recaptcha-container');
        if (!containerElement) {
          initAttempts++;
          if (initAttempts < maxAttempts) {
            console.log(`reCAPTCHA container not ready, attempt ${initAttempts}/${maxAttempts}`);
            setTimeout(initRecaptcha, 200);
          } else {
            console.error('reCAPTCHA container element not found after maximum attempts');
            setIsRecaptchaLoading(false);
          }
          return;
        }

        // Check if reCAPTCHA is already rendered in this container
        if (containerElement.hasChildNodes()) {
          console.log('reCAPTCHA already rendered, skipping initialization');
          setIsRecaptchaLoading(false);
          return;
        }

        const widgetId = window.grecaptcha.render('recaptcha-container', {
          sitekey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LfGOq4qAAAAAKEQ7lFhLKNRo7LPk_X_UHF1Q4n-', // Fallback to production key
          callback: (token: string) => {
            console.log('reCAPTCHA token received:', !!token);
            setRecaptchaToken(token);
            setIsRecaptchaLoading(false);
          },
          'expired-callback': () => {
            console.log('reCAPTCHA token expired');
            setRecaptchaToken('');
          },
          'error-callback': () => {
            console.log('reCAPTCHA error occurred');
            setRecaptchaToken('');
            setIsRecaptchaLoading(false);
          }
        });
        setRecaptchaWidgetId(widgetId);
        setIsRecaptchaLoading(false);
        console.log('reCAPTCHA initialized successfully');
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
        initAttempts++;
        if (initAttempts < maxAttempts) {
          setTimeout(initRecaptcha, 500);
        } else {
          setIsRecaptchaLoading(false);
        }
      }
    };

    // Try to initialize immediately if grecaptcha is available
    if (typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(initRecaptcha);
    } else {
      // Fallback polling for script load
      setTimeout(initRecaptcha, 100);
    }

    // Cleanup function
    return () => {
      if (recaptchaWidgetId !== null && typeof window !== 'undefined' && window.grecaptcha) {
        try {
          window.grecaptcha.reset(recaptchaWidgetId);
        } catch (error) {
          console.error('Error resetting reCAPTCHA:', error);
        }
      }
    };
  }, [shouldShowRecaptcha, recaptchaWidgetId]);

  // Reset reCAPTCHA when needed
  const resetRecaptcha = () => {
    if (recaptchaWidgetId !== null && window.grecaptcha) {
      window.grecaptcha.reset(recaptchaWidgetId);
      setRecaptchaToken('');
    }
  };

  // Verify reCAPTCHA token with backend
  const verifyRecaptcha = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/verify-recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error verifying reCAPTCHA:', error);
      return false;
    }
  };

  const handleNameSubmitWithCaptcha = async () => {
    if (!isContinueEnabled()) return;

    // For interests and context questions, submit directly without reCAPTCHA
    if (question.type === 'interests' || question.type === 'context' || 
        question.type === 'current_joy' || question.type === 'problem_solve' || 
        question.type === 'free_text_persona' || question.type === 'treat_for_self' ||
        question.type === 'desired_emotion' || question.type === 'tone_keyword') {
      onAnswer(textInput.trim());
      return;
    }

    // If reCAPTCHA is disabled or not required, proceed directly
    if (!shouldShowRecaptcha) {
      onNameSubmit();
      return;
    }

    setIsVerifyingRecaptcha(true);

    try {
      const isValid = await verifyRecaptcha(recaptchaToken);
      if (isValid) {
        onNameSubmit();
      } else {
        alert('reCAPTCHA verification failed. Please try again.');
        resetRecaptcha();
      }
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      alert('reCAPTCHA verification error. Please try again.');
      resetRecaptcha();
    } finally {
      setIsVerifyingRecaptcha(false);
    }
  };

  // Pre-populate inputs with previous answers
  useEffect(() => {
    const existingAnswer = answers[question.id];
    if (existingAnswer && (question.type === 'name' || question.type === 'name_and_culture')) {
      setNameInput(String(existingAnswer));
    }
    if (existingAnswer && (question.type === 'interests' || question.type === 'context' || 
        question.type === 'current_joy' || question.type === 'problem_solve' || 
        question.type === 'free_text_persona' || question.type === 'treat_for_self' ||
        question.type === 'desired_emotion' || question.type === 'tone_keyword')) {
      setTextInput(String(existingAnswer));
    }
    if (existingAnswer && question.type === 'slider' && typeof existingAnswer === 'number') {
      setSliderValue(existingAnswer);
    }
    if (existingAnswer && question.type === 'budget_with_flex' && typeof existingAnswer === 'object') {
      const budgetAnswer = existingAnswer as { budget: string; flexibility: string };
      if (budgetAnswer.budget) {
        setTextInput(budgetAnswer.budget);
      }
    }
  }, [question.id, answers, question.type, setNameInput]);

  // Clear additional input when switching questions
  useEffect(() => {
    setAdditionalInput('');
    setQuestion9SelectedAnswer(null);
  }, [question.id]);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
  };

  const handleSliderSubmit = () => {
    onAnswer(sliderValue);
  };

  // Check if continue button should be enabled
  const isContinueEnabled = () => {
    const currentInput = (question.type === 'name' || question.type === 'name_and_culture') ? nameInput : textInput;
    if (!currentInput.trim()) return false;
    // Only require reCAPTCHA token if reCAPTCHA is enabled (only for name questions)
    if (shouldShowRecaptcha && (question.type === 'name' || question.type === 'name_and_culture') && !recaptchaToken) return false;
    if (isVerifyingRecaptcha) return false;
    return true;
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
      {/* Back Button */}
      {canGoBack && onGoBack && (
        <div className="mb-4">
          <button
            onClick={onGoBack}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      )}

      <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center leading-relaxed">
        {question.text}
      </h2>

      {/* Text Input Field (for name, interests, dates, etc.) */}
      {(question.type === 'name' || question.type === 'interests' || question.type === 'context' || 
        question.type === 'name_and_culture' || question.type === 'desired_emotion' || 
        question.type === 'current_joy' || question.type === 'problem_solve' || 
        question.type === 'free_text_persona' || question.type === 'treat_for_self' ||
        question.type === 'tone_keyword') && (
        <div className="mb-8">
          <div className="max-w-md mx-auto">
            <input
              type="text"
              value={question.type === 'name' || question.type === 'name_and_culture' ? nameInput : textInput}
              onChange={(e) => {
                const maxLength = 
                  (question.type === 'name' || question.type === 'name_and_culture') && question.id === 1 ? 50 : // Name limit
                  question.type === 'name' && question.text.includes('birthday') ? 100 : // Dates limit
                  question.type === 'interests' ? 500 : // Interests limit
                  question.type === 'context' ? 250 : // Context limit
                  question.type === 'current_joy' || question.type === 'problem_solve' || question.type === 'free_text_persona' || question.type === 'treat_for_self' ? 300 : // Discovery fields
                  question.type === 'desired_emotion' || question.type === 'tone_keyword' ? 150 : // Shorter emotional responses
                  200; // Default limit
                
                if (e.target.value.length <= maxLength) {
                  if (question.type === 'name' || question.type === 'name_and_culture') {
                    setNameInput(e.target.value);
                  } else {
                    setTextInput(e.target.value);
                  }
                }
              }}
              placeholder={
                (question.type === 'name' || question.type === 'name_and_culture') && question.id === 1 ? "Enter your name..." :
                question.type === 'interests' ? "Include: main interests, skill level (casual/serious), any restrictions (allergies, dietary, space), best/worst gifts they've received, experience vs physical preference..." :
                question.type === 'context' ? "Any special details about this occasion..." :
                question.type === 'current_joy' ? "What's bringing them happiness right now..." :
                question.type === 'problem_solve' ? "Any struggles or challenges they've mentioned..." :
                question.type === 'free_text_persona' ? "How would you describe them to a stranger..." :
                question.type === 'treat_for_self' ? "Something they'd love but wouldn't buy themselves..." :
                question.type === 'desired_emotion' ? "What feeling do you want to create..." :
                question.type === 'tone_keyword' ? "One word that captures the perfect tone..." :
                question.type === 'name' && question.text.includes('birthday') ? "e.g., March 15, December 8..." :
                "Enter your response..."
              }
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none text-center text-lg transition-colors"
              onKeyPress={(e) => e.key === 'Enter' && isContinueEnabled() && handleNameSubmitWithCaptcha()}
            />
            
            {/* Character count for text inputs */}
            <div className="text-right mt-1 text-xs text-gray-400">
              {(question.type === 'name' || question.type === 'name_and_culture' ? nameInput : textInput).length}/{
                (question.type === 'name' || question.type === 'name_and_culture') && question.id === 1 ? 50 :
                question.type === 'name' && question.text.includes('birthday') ? 100 :
                question.type === 'interests' ? 500 :
                question.type === 'context' ? 250 :
                question.type === 'current_joy' || question.type === 'problem_solve' || question.type === 'free_text_persona' || question.type === 'treat_for_self' ? 300 :
                question.type === 'desired_emotion' || question.type === 'tone_keyword' ? 150 :
                200
              } characters
            </div>

            {/* reCAPTCHA for first question */}
            {shouldShowRecaptcha && (
              <div className="mt-4">
                <div className="flex justify-center">
                  <div className="relative">
                    {/* Always render the container div */}
                    <div 
                      id="recaptcha-container" 
                      style={{ 
                        transform: 'scale(0.85)', 
                        transformOrigin: 'center',
                        marginBottom: '-10px'
                      }}
                    ></div>
                    
                    {/* Show loading overlay while initializing */}
                    {isRecaptchaLoading && (
                      <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
                        <div className="text-center p-4">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          <p className="text-sm text-gray-500 mt-2">Loading verification...</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Show warning if failed to load */}
                    {recaptchaWidgetId === null && !isRecaptchaLoading && (
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200 mt-2">
                        <p className="text-sm text-yellow-700">
                          Verification loading slowly. Try refreshing the page if it doesn't appear.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleNameSubmitWithCaptcha}
              disabled={!isContinueEnabled()}
              className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              {isVerifyingRecaptcha ? 'Verifying...' : 'Continue'}
            </button>

            {/* Helper text for reCAPTCHA */}
            {shouldShowRecaptcha && !recaptchaToken && nameInput.trim() && question.type === 'name' && (
              <p className="text-sm text-gray-500 text-center mt-2">
                Please complete the verification above to continue
              </p>
            )}
          </div>
          
          {/* Hint suggestions for text inputs */}
          {question.hints && question.hints.length > 0 && (
            <>
              <div className="text-center my-6">
                <span className="text-gray-400 text-sm">or choose a suggestion:</span>
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                {question.hints.map((hint, index) => (
                  <button
                    key={typeof hint === 'object' ? `${hint.icon}-${hint.text}` : `${hint}-${index}`}
                    onClick={() => {
                      const hintText = typeof hint === 'string' ? hint : hint.text;
                      
                      if (question.type === 'name' || question.type === 'name_and_culture') {
                        setNameInput(hintText);
                      } else {
                        setTextInput(hintText);
                      }
                      
                      // For Question 217 (gift stories), don't proceed immediately unless it's "Skip this question"
                      if (question.id === 217 && hintText !== "Skip this question") {
                        // Just populate the text, don't proceed
                        return;
                      }
                      
                      // For other questions, proceed immediately
                      onAnswer(hint);
                    }}
                    className="px-4 py-2 rounded-full border-2 bg-gray-50 border-gray-200 hover:border-indigo-300 text-gray-700 transition-all"
                  >
                    {typeof hint === 'string' ? hint : `${hint.icon} ${hint.text}`}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Multi-Slider Component for personality_sliders */}
      {question.type === 'personality_sliders' && Array.isArray(question.sliderConfig) && (
        <div className="mb-8">
          <div className="max-w-2xl mx-auto space-y-6">
            {question.sliderConfig.map((slider) => {
              const currentAnswer = answers[question.id] as { [key: string]: number } || {};
              const sliderValue = currentAnswer[slider.key] || 50;
              
              return (
                <div key={slider.key} className="bg-gray-50 p-4 rounded-xl">
                  <h4 className="text-lg font-medium text-gray-800 mb-3 text-center">
                    {slider.label}
                  </h4>
                  <div className="flex justify-between text-sm text-gray-600 mb-3">
                    <span>Less</span>
                    <span>More</span>
                  </div>
                  <div className="relative mb-2">
                    <input
                      type="range"
                      min={slider.min}
                      max={slider.max}
                      value={sliderValue}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        const newAnswer = { ...currentAnswer, [slider.key]: newValue };
                        onAnswer(newAnswer);
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${sliderValue}%, #4f46e5 ${sliderValue}%, #4f46e5 100%)`
                      }}
                    />
                  </div>
                  <div className="text-center">
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                      {sliderValue}%
                    </span>
                  </div>
                </div>
              );
            })}
            
            {/* Continue button for multi-sliders */}
            <div className="text-center pt-4">
              <button
                onClick={() => {
                  // Continue with current slider values - they're already stored in answers
                  const currentAnswer = answers[question.id] as { [key: string]: number } || {};
                  onAnswer(currentAnswer);
                }}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget with Flexibility Component */}
      {question.type === 'budget_with_flex' && (
        <div className="mb-8">
          <div className="max-w-md mx-auto space-y-4">
            {/* Budget Range Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
              <input
                type="text"
                value={textInput}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setTextInput(e.target.value);
                  }
                }}
                placeholder="e.g., $50-100, under $200, around $75..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none text-center"
              />
            </div>
            
            {/* Flexibility Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">How flexible is this budget?</label>
              <div className="flex gap-3 justify-center">
                {['firm', 'flexible'].map((flexibility) => {
                  const currentAnswer = answers[question.id] as { budget: string; flexibility: string } || {};
                  const isSelected = currentAnswer.flexibility === flexibility;
                  
                  return (
                    <button
                      key={flexibility}
                      onClick={() => {
                        const newAnswer = { 
                          budget: textInput || currentAnswer.budget || '', 
                          flexibility 
                        };
                        onAnswer(newAnswer);
                      }}
                      className={`px-4 py-2 rounded-full border-2 transition-all capitalize ${
                        isSelected
                          ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                          : 'bg-gray-50 border-gray-200 hover:border-indigo-300 text-gray-700'
                      }`}
                    >
                      {flexibility}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Continue button */}
            <button
              onClick={() => {
                const currentAnswer = answers[question.id] as { budget: string; flexibility: string } || {};
                if (textInput.trim() && currentAnswer.flexibility) {
                  onAnswer({ budget: textInput.trim(), flexibility: currentAnswer.flexibility });
                }
              }}
              disabled={!textInput.trim() || !(answers[question.id] as { budget: string; flexibility: string })?.flexibility}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Slider Component */}
      {question.type === 'slider' && !Array.isArray(question.sliderConfig) && (
        <div className="mb-8">
          <div className="max-w-lg mx-auto">
            <div className="flex justify-between text-sm text-gray-600 mb-4">
              <span>{question.sliderConfig?.labels?.[0]}</span>
              <span>{question.sliderConfig?.labels?.[1]}</span>
            </div>
            <div className="relative">
              <input
                type="range"
                min={question.sliderConfig?.min || 0}
                max={question.sliderConfig?.max || 100}
                step={question.sliderConfig?.step || 10}
                value={sliderValue}
                onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${sliderValue}%, #4f46e5 ${sliderValue}%, #4f46e5 100%)`
                }}
              />
              <div className="flex justify-center mt-4">
                <span className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                  {sliderValue}% toward {question.sliderConfig?.labels?.[1]}
                </span>
              </div>
            </div>
            {question.hints && (
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{typeof question.hints[0] === 'string' ? question.hints[0] : question.hints[0]?.text}</span>
                <span>{typeof question.hints[1] === 'string' ? question.hints[1] : question.hints[1]?.text}</span>
              </div>
            )}
            <button
              onClick={handleSliderSubmit}
              className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Single-select questions */}
      {!question.multiple && 
       question.type !== 'name' && question.type !== 'interests' && question.type !== 'context' && 
       question.type !== 'slider' && question.type !== 'personality_sliders' && 
       question.type !== 'budget_with_flex' &&
       question.type !== 'name_and_culture' && question.type !== 'desired_emotion' && 
       question.type !== 'current_joy' && question.type !== 'problem_solve' && 
       question.type !== 'free_text_persona' && question.type !== 'treat_for_self' &&
       question.type !== 'tone_keyword' && (
        <div>
          <div className="flex flex-wrap gap-3 justify-center mb-4">
            {question.hints?.map((hint, index) => (
              <button
                key={typeof hint === 'object' ? `${hint.icon}-${hint.text}` : `${hint}-${index}`}
                onClick={() => {
                  // For the last question (question 9 in giver flow), just store the answer but don't auto-advance
                  if (question.id === 9) {
                    setQuestion9SelectedAnswer(hint);
                  } else {
                    // For other questions, auto-advance as before
                    onAnswer(hint);
                  }
                }}
                className={`px-4 py-2 rounded-full border-2 transition-all ${
                  (question.id === 9 ? question9SelectedAnswer === hint : answers[question.id] === hint)
                    ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                    : 'bg-gray-50 border-gray-200 hover:border-indigo-300 text-gray-700'
                }`}
              >
                {typeof hint === 'object' ? `${hint.icon} ${hint.text}` : hint}
              </button>
            ))}
          </div>
          
          {/* Show continue button for the last question (question 9) */}
          {question.id === 9 && question9SelectedAnswer && (
            <div className="text-center">
              <button
                onClick={() => !isAnalyzing && onAnswer(question9SelectedAnswer)}
                disabled={isAnalyzing}
                className="inline-flex items-center gap-2 px-8 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors text-lg"
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Analyzing Your Style...
                  </>
                ) : (
                  <>
                    Analyze My Gift-Giving Style
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-2">
                {isAnalyzing 
                  ? "Our AI is analyzing your responses using psychological frameworks..."
                  : "This will analyze your responses and create your personality profile"
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Multi-select questions */}
      {question.multiple && (
        <div>
          <div className="flex flex-wrap gap-3 justify-center mb-4">
            {question.hints?.map((hint, index) => {
              const currentSelections = Array.isArray(answers[question.id]) 
                ? answers[question.id] as (string | OptionHint)[]
                : [];
              const isSelected = currentSelections.some((item) => 
                typeof item === 'object' && typeof hint === 'object' && item !== null && hint !== null && 'text' in item && 'text' in hint
                  ? item.text === hint.text 
                  : item === hint
              );
              
              const uniqueKey = typeof hint === 'object' ? `${hint.icon}-${hint.text}` : `${hint}-${index}`;
              
              return (
                <button
                  key={uniqueKey}
                  onClick={() => onAnswer(hint)}
                  className={`px-4 py-2 rounded-full border-2 transition-all ${
                    isSelected
                      ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                      : 'bg-gray-50 border-gray-200 hover:border-indigo-300 text-gray-700'
                  }`}
                >
                  {typeof hint === 'object' ? `${hint.icon} ${hint.text}` : hint}
                </button>
              );
            })}
          </div>
          
          {/* Show "Other restrictions" text input if selected for structured_interests */}
          {question.type === 'structured_interests' && Array.isArray(answers[question.id]) && 
           (answers[question.id] as (string | OptionHint)[]).some((item) => 
             typeof item === 'object' && item !== null && 'text' in item && item.text === 'Other restrictions'
           ) && (
            <div className="max-w-md mx-auto bg-blue-50 rounded-xl p-4 mb-4">
              <label className="block text-sm font-medium text-blue-800 mb-2">
                Please specify other restrictions:
              </label>
              <input
                type="text"
                value={additionalInput}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setAdditionalInput(e.target.value);
                  }
                }}
                placeholder="e.g., no scented products, limited storage space..."
                className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              />
              <div className="text-right mt-1 text-xs text-blue-600">
                {additionalInput.length}/200 characters
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-500 text-center mb-4">
            Select up to {question.maxSelections} • {Array.isArray(answers[question.id]) ? (answers[question.id] as (string | OptionHint)[]).length : 0} selected
          </p>
          
          {Array.isArray(answers[question.id]) && (answers[question.id] as (string | OptionHint)[]).length > 0 && (
            <div className="text-center">
              <button
                onClick={() => onContinueValues(additionalInput.trim())}
                className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 